// Resource limits for browser preferences, not search-result limits.
export const storageSafetyLimits = Object.freeze({
  maxCollectionEntries: 1000,
  maxDefaultCharacters: 256 * 1024,
  maxDetailCharacters: 1024 * 1024,
  maxDepth: 32,
  maxNodes: 20000,
  maxStringCharacters: 8192,
});

export function storageBaseKey(key) {
  return String(key).replace(
    /^(?:catchy_)?(?:wellington|auckland|chch|otago)_/,
    "",
  );
}
const reservedKeys = new Set(["__proto__", "constructor", "prototype"]);
const validId = (id) =>
  typeof id === "string" &&
  id.trim().length > 0 &&
  id.length <= 200 &&
  !reservedKeys.has(id);
const plainRecord = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const maxCharacters = (key) =>
  ["favourite_stop_details", "preferred_stop_names"].includes(
    storageBaseKey(key),
  )
    ? storageSafetyLimits.maxDetailCharacters
    : storageSafetyLimits.maxDefaultCharacters;

function isBoundedTree(value, characterLimit) {
  const stack = [{ value, depth: 0 }];
  const ancestors = new Set();
  let nodes = 0;
  let characters = 0;
  while (stack.length) {
    const { value: item, depth, exit } = stack.pop();
    if (exit) {
      ancestors.delete(item);
      continue;
    }
    if (
      ++nodes > storageSafetyLimits.maxNodes ||
      depth > storageSafetyLimits.maxDepth
    )
      return false;
    if (
      typeof item === "string" &&
      item.length > storageSafetyLimits.maxStringCharacters
    )
      return false;
    if (typeof item === "number" && !Number.isFinite(item)) return false;
    if (
      item === null ||
      ["string", "number", "boolean"].includes(typeof item)
    ) {
      characters += JSON.stringify(item).length;
      if (characters > characterLimit) return false;
      continue;
    }
    if (typeof item !== "object" || ancestors.has(item)) return false;
    ancestors.add(item);
    stack.push({ value: item, depth, exit: true });
    if (
      Array.isArray(item) &&
      item.length > storageSafetyLimits.maxCollectionEntries
    )
      return false;
    const keys = Object.keys(item);
    characters += 2 + keys.length;
    if (!Array.isArray(item))
      for (const key of keys) characters += JSON.stringify(key).length + 1;
    if (characters > characterLimit) return false;
    if (keys.length > storageSafetyLimits.maxCollectionEntries) return false;
    for (const key of keys) {
      if (key.length > 256 || reservedKeys.has(key)) return false;
      stack.push({ value: item[key], depth: depth + 1 });
    }
  }
  return true;
}

export function validateStoredValue(key, value, fallback) {
  if (!isBoundedTree(value, maxCharacters(key))) return { ok: false };
  const baseKey = storageBaseKey(key);
  if (baseKey === "favourites") {
    if (!Array.isArray(value)) return { ok: false };
    // Accept old object references as well as the current exact-ID array.
    const ids = value
      .map((entry) =>
        plainRecord(entry) ? (entry.stop_id ?? entry.sms) : entry,
      )
      .filter(validId);
    return { ok: true, value: [...new Set(ids)] };
  }
  if (baseKey === "preferred_stop_names") {
    if (!Array.isArray(value)) return { ok: false };
    return {
      ok: true,
      value: value.filter(
        (entry) =>
          plainRecord(entry) &&
          validId(entry.stop_id ?? entry.sms) &&
          typeof entry.name === "string" &&
          entry.name.length <= 1024,
      ),
    };
  }
  if (baseKey === "favourite_stop_details") {
    if (!plainRecord(value)) return { ok: false };
    const entries = Object.entries(value).filter(
      ([id, row]) =>
        validId(id) &&
        plainRecord(row) &&
        row.stop_id === id &&
        typeof row.stop_name === "string" &&
        row.stop_name.length > 0 &&
        row.stop_name.length <= 1024 &&
        (row.stop_type == null ||
          ["bus", "train", "ferry", "cable_car"].includes(row.stop_type)) &&
        (row.stop_code == null ||
          (typeof row.stop_code === "string" && row.stop_code.length <= 200)) &&
        ["stop_lat", "stop_lon"].every(
          (field) =>
            row[field] == null ||
            (typeof row[field] === "number" &&
              Number.isFinite(row[field]) &&
              Math.abs(row[field]) <= (field === "stop_lat" ? 90 : 180)),
        ),
    );
    return { ok: true, value: Object.fromEntries(entries) };
  }
  if (
    (Array.isArray(fallback) && !Array.isArray(value)) ||
    (typeof fallback === "boolean" && typeof value !== "boolean") ||
    (typeof fallback === "number" && typeof value !== "number") ||
    (typeof fallback === "string" && typeof value !== "string")
  )
    return { ok: false };
  return { ok: true, value };
}

export function parseStoredValue(key, raw, fallback) {
  if (typeof raw !== "string" || raw.length > maxCharacters(key))
    return { ok: false };
  try {
    return validateStoredValue(key, JSON.parse(raw), fallback);
  } catch {
    return { ok: false };
  }
}

export function serializeStoredValue(key, value, fallback) {
  try {
    const checked = validateStoredValue(key, value, fallback);
    if (!checked.ok) return { ok: false };
    const raw = JSON.stringify(checked.value);
    return raw.length <= maxCharacters(key)
      ? { ...checked, raw }
      : { ok: false };
  } catch {
    return { ok: false };
  }
}

export function getBrowserStorage(kind = "localStorage") {
  try {
    return typeof window === "undefined" ? null : window[kind];
  } catch {
    return null;
  }
}
export function safeStorageGet(storage, key) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}
export function safeStorageSet(storage, key, value) {
  try {
    if (!storage) return false;
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}
export function safeStorageRemove(storage, key) {
  try {
    if (!storage) return false;
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}
